import React, { useEffect, useState } from 'react';
import { makeStyles } from 'design-system/product/theme';

// Components
import {
  Button,
  RadioGroup,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Select,
  Option,
  Checkbox,
} from 'design-system';
import {
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
} from 'react-scale/components/design-system/product';
// eslint-disable-next-line restrict-imports/restrict-imports
import CloseIcon from '@material-ui/icons/Close';
import _ from 'lodash';

// Types
import { AuditResult, ReviewLevel } from '@scale/shared/defaults';
import { AuditResultAction, AuditResultActionType } from '@scale/shared/types/corp/attemptAudit';
import SAlert from 'react-s-alert';

import { GridColumns } from '@mui/x-data-grid-pro';
import {
  IMandatoryColumn,
  ConditionOperations,
} from '@scale/shared/types/corp/speedAuditColumnConfig';
import Paper from '@mui/material/Paper';

const defaultNoAction = {
  type: AuditResultActionType.NO_ACTION,
};

const defaultChangeAndDoNotSend = {
  type: AuditResultActionType.CHANGE_WITHOUT_SENDING,
  reviewLevel: ReviewLevel.Attempt,
  clearResponse: true,
};

const defaultSendBack = {
  type: AuditResultActionType.SEND_BACK_TO_QUEUE,
  reviewLevel: ReviewLevel.Attempt,
  clearResponse: true,
};

const actionOptions = [
  { label: 'No action', value: AuditResultActionType.NO_ACTION },
  {
    label:
      'Apply changes and add a review to FIXED tasks with this rating (this progresses the pipeline stage)',
    value: AuditResultActionType.CHANGE_FIXED_WITHOUT_SENDING,
  },
  {
    label:
      'Apply changes and add a review to ALL tasks with this rating (this progresses the pipeline stage)',
    value: AuditResultActionType.CHANGE_WITHOUT_SENDING,
  },
  {
    label: 'Apply fixes and send to customer',
    value: AuditResultActionType.CHANGE_AND_SEND,
  },
  {
    label: 'Send back to queue (keeps original response)',
    value: AuditResultActionType.SEND_BACK_TO_QUEUE,
  },
];

const setRowsWithMandatoryErrors = (apiRef: $FIXME, mandatoryColumns: IMandatoryColumn) => {
  const rowsWithMandatoryErrors: { [taskId: string]: string[] } = {};
  const mandatoryColumnsEntries = Object.entries(mandatoryColumns);
  if (!_.isEmpty(apiRef.current)) {
    const updatedRows = apiRef.current.getRowModels();
    Array.from(updatedRows.values()).forEach((row: $FIXME) => {
      for (const [columnName, conditionConfig] of mandatoryColumnsEntries) {
        let hasConditionalError = false;
        if (conditionConfig.isMandatory) {
          const columnValue = _.get(row, columnName);
          if (_.isObject(columnValue) ? _.isEmpty(columnValue) : !columnValue) {
            if (
              conditionConfig.isConditional &&
              conditionConfig.conditionalColumn &&
              conditionConfig.condition
            ) {
              const conditionColumnValue = _.get(row, conditionConfig.conditionalColumn, '');
              hasConditionalError = _.get(
                ConditionOperations,
                conditionConfig.condition,
                (conditionVavlue: $FIXME) => !!conditionVavlue,
              )(conditionColumnValue, conditionConfig.conditionValue);
            } else {
              hasConditionalError = true;
            }
            if (hasConditionalError) {
              const taskId = row.task_id;
              if (rowsWithMandatoryErrors[taskId]) {
                rowsWithMandatoryErrors[taskId].push(columnName);
              } else {
                rowsWithMandatoryErrors[taskId] = [columnName];
              }
            }
          }
        }
      }
    });
  }
  return rowsWithMandatoryErrors;
};

const MandatoryColumnTooltip = ({
  field,
  mandatoryColumns,
  columns,
}: {
  field: string;
  mandatoryColumns: IMandatoryColumn;
  columns: GridColumns;
}) => {
  const mandatoryColumn = _.get(mandatoryColumns, field);
  const header = columns.find(c => c.field === field)?.headerName || field;
  return (
    <Tooltip
      key={field}
      title={
        _.get(mandatoryColumn, 'isMandatory')
          ? `${header} is mandatory ${
              mandatoryColumn.isConditional
                ? `if the column ${_.startCase(
                    (mandatoryColumn?.conditionalColumn ?? '').replace('_editable', ''),
                  )} ${mandatoryColumn.condition} ${mandatoryColumn.conditionValue}`
                : ''
            }`
          : ''
      }
    >
      <Chip color="primary" variant="outlined" label={header} />
    </Tooltip>
  );
};

interface SubmitAuditModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  setIsSubmitting: (value: boolean) => void;
  completeAudit: (actions: Record<string, any>, sendFeedback: boolean) => void;
  canEditResponses: boolean;
  usesPipelineV3: boolean; // TODO: Remove this once we find a way to support editing & completing tasks
  completionCounts: Record<string, number>;
  mandatoryColumns: IMandatoryColumn;
  apiRef: $FIXME;
  columns: GridColumns;
}

const SubmitAuditModal: React.FC<SubmitAuditModalProps> = ({
  isOpen,
  setIsOpen,
  setIsSubmitting,
  completeAudit,
  canEditResponses,
  usesPipelineV3,
  completionCounts,
  mandatoryColumns,
  apiRef,
  columns,
}) => {
  const classes = useStyles();

  const [perfectAction, setPerfectAction] = useState<AuditResultAction>(defaultChangeAndDoNotSend);
  const [goodAction, setGoodAction] = useState<AuditResultAction>(defaultChangeAndDoNotSend);
  const [okAction, setOkAction] = useState<AuditResultAction>(defaultChangeAndDoNotSend);
  const [badAction, setBadAction] = useState<AuditResultAction>(defaultSendBack);
  const [terribleAction, setTerribleAction] = useState<AuditResultAction>(defaultSendBack);
  const [noRatingAction, setNoRatingAction] =
    useState<AuditResultAction>(defaultChangeAndDoNotSend);
  const [sendFeedback, setSendFeedback] = useState<boolean>(false);
  const rowsWithMandatoryErrors = setRowsWithMandatoryErrors(apiRef, mandatoryColumns);

  const submitHelper = async () => {
    setIsOpen(false);
    setIsSubmitting(true);
    try {
      await completeAudit(
        {
          perfect: perfectAction,
          good: goodAction,
          ok: okAction,
          bad: badAction,
          terrible: terribleAction,
          noRating: noRatingAction,
        },
        sendFeedback,
      );
    } catch (e) {
      SAlert.error(e.message);
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!canEditResponses) {
      setPerfectAction(defaultNoAction);
      setGoodAction(defaultNoAction);
      setOkAction(defaultNoAction);
      setBadAction(defaultNoAction);
      setTerribleAction(defaultNoAction);
      setNoRatingAction(defaultNoAction);
    }
  }, [canEditResponses]);

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className={classes.close}>
          <IconButton aria-label="close" size="small" onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </div>
        <Typography className={classes.header}>Complete Audit</Typography>
        <Typography className={classes.subtitle}>
          Complete the audit and take actions on tasks based on your ratings.
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {_.isEmpty(rowsWithMandatoryErrors) ? (
          <div>
            <div>
              <Checkbox
                label=""
                checked={sendFeedback}
                onChange={e => setSendFeedback(e.target.checked)}
              />
              Send feedback to taskers as a course or review feedback (if a review is added)
            </div>
            <List>
              {['No rating'].concat(_.values(AuditResult)).map(score => {
                let action;
                let setAction: any;
                switch (score) {
                  case 'No rating':
                    action = noRatingAction;
                    setAction = setNoRatingAction;
                    break;
                  case AuditResult.Perfect:
                    action = perfectAction;
                    setAction = setPerfectAction;
                    break;
                  case AuditResult.Good:
                    action = goodAction;
                    setAction = setGoodAction;
                    break;
                  case AuditResult.Ok:
                    action = okAction;
                    setAction = setOkAction;
                    break;
                  case AuditResult.Bad:
                    action = badAction;
                    setAction = setBadAction;
                    break;
                  default:
                    action = terribleAction;
                    setAction = setTerribleAction;
                }
                return (
                  <ListItem key={score} className={classes.ratingModal}>
                    <ListItemText className={classes.ratingRow}>
                      {score === 'No rating' ? 'No rating' : `${_.capitalize(score)} Tasks`} (
                      {_.get(completionCounts, score, 0)} tasks)
                    </ListItemText>
                    <Select
                      value={action.type}
                      disabled={!canEditResponses}
                      onChange={event => {
                        setAction((currentValue: AuditResultAction) => ({
                          ...currentValue,
                          type: event.target.value,
                        }));
                      }}
                    >
                      {actionOptions
                        .filter(
                          opt =>
                            !usesPipelineV3 || opt.value !== AuditResultActionType.CHANGE_AND_SEND,
                        )
                        .map(opt => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                    </Select>
                    <div className={classes.actionRow}>
                      {action.type === AuditResultActionType.SEND_BACK_TO_QUEUE && (
                        <div>
                          <Typography variant="subtitle2">Clear Responses?</Typography>
                          <RadioGroup
                            value={(action.clearResponse ?? true).toString()}
                            onChange={(value: string) =>
                              setAction((currentValue: AuditResultAction) => ({
                                ...currentValue,
                                clearResponse: value === 'true',
                              }))
                            }
                          >
                            <Radio
                              key="clear-response"
                              value="true"
                              label="Yes (send back to attempt)"
                              labelPlacement="end"
                            ></Radio>
                            <Radio
                              key="keep-response"
                              value="false"
                              label="No (specify review level)"
                              labelPlacement="end"
                            ></Radio>
                          </RadioGroup>
                        </div>
                      )}
                      {action.type === AuditResultActionType.SEND_BACK_TO_QUEUE &&
                        action.clearResponse === false && (
                          <div>
                            <Typography variant="subtitle2">
                              Review level to send back to:
                            </Typography>
                            <RadioGroup
                              value={(action.reviewLevel ?? -1).toString()}
                              onChange={(value: string) =>
                                setAction((currentValue: AuditResultAction) => ({
                                  ...currentValue,
                                  reviewLevel: parseInt(value) as ReviewLevel,
                                }))
                              }
                            >
                              <Radio
                                key="attempt"
                                value="-1"
                                label="Attempt"
                                labelPlacement="end"
                              ></Radio>
                              <Radio
                                key="L0"
                                value="0"
                                label="L0"
                                labelPlacement="end"
                                disabled={action.clearResponse}
                              ></Radio>
                              <Radio
                                key="L1"
                                value="1"
                                label="L1"
                                labelPlacement="end"
                                disabled={action.clearResponse}
                              ></Radio>
                              <Radio
                                key="L8"
                                value="8"
                                label="L8"
                                labelPlacement="end"
                                disabled={action.clearResponse}
                              ></Radio>
                              <Radio
                                key="L10"
                                value="10"
                                label="L10"
                                labelPlacement="end"
                                disabled={action.clearResponse}
                              ></Radio>
                              <Radio
                                key="L11"
                                value="11"
                                label="L11"
                                labelPlacement="end"
                                disabled={action.clearResponse}
                              ></Radio>
                              <Radio
                                key="L12"
                                value="12"
                                label="L12"
                                labelPlacement="end"
                                disabled={action.clearResponse}
                              ></Radio>
                            </RadioGroup>
                          </div>
                        )}
                    </div>
                  </ListItem>
                );
              })}
            </List>
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Task ID</TableCell>
                  <TableCell>Columns with Errors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(rowsWithMandatoryErrors).map(([taskId, columnErrors]) => (
                  <TableRow key={taskId}>
                    <TableCell component="th" scope="row">
                      {taskId}
                    </TableCell>
                    <TableCell>
                      <div className={classes.errorColumn}>
                        {columnErrors.map(columnError => (
                          <MandatoryColumnTooltip
                            key={columnError}
                            field={columnError}
                            mandatoryColumns={mandatoryColumns}
                            columns={columns}
                          />
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.button}
          variant="outlined"
          type="secondary"
          onClick={() => setIsOpen(false)}
        >
          Cancel
        </Button>
        <Button
          className={classes.submitButton}
          type="primary"
          onClick={submitHelper}
          disabled={!_.isEmpty(rowsWithMandatoryErrors)}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles({
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  ratingModal: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  ratingRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '16px',
  },
  actionRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '16px',
    marginTop: '8px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    color: 'gray',
  },
  close: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: () => 'gray',
  },
  submitButton: {
    padding: '6px 16px !important',
    color: 'white',
    background: 'rgb(25, 118, 210, 1)',
    '&:hover': {
      background: 'rgb(25, 118, 210, 0.75)',
    },
  },
  button: {
    color: 'rgb(25, 118, 210, 1)',
    background: 'white',
    borderColor: 'rgb(25, 118, 210, 1)',
    padding: '6px 16px !important',
    '&:hover': {
      background: 'rgb(25, 118, 210, 0.25)',
    },
  },
  editableColumnItem: {
    borderColor: 'white',
    gap: '8px',
    alignItems: 'center',
  },
  errorColumn: {
    gap: '5px',
    display: 'flex',
    flexWrap: 'wrap',
  },
});

export default SubmitAuditModal;
